import { IconRefresh } from "@tabler/icons-react";
import { useEffect, useState, type FC, type ReactNode } from "react";
import { tv } from "tailwind-variants";
import { ButtonVariant } from "../Buttons/button.js";
import { IconButton } from "../Buttons/iconButton.js";
import { Size } from "../helpers/helpers.js";

export interface MessageItemProps {
  size?: typeof Size.SMALL | typeof Size.MEDIUM;
  role: "user" | "assistant" | "tool";
  /** See the `layout` style variant. */
  layout?: "simulation" | "agent";
  /** Set when the bubble renders Markdown (see the `prose` style variant). */
  prose?: boolean;
  isSaving?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  children: ReactNode;
}

const SAVING_INDICATOR_DEBOUNCE_INTERVAL = 2000; // ms

export const messageItemStyle = tv({
  slots: {
    row: "flex",
    bubble: "max-w-[80%] rounded-lg px-3.5 py-2",
  },
  variants: {
    /**
     * Bubbles rendering Markdown (e.g. `MarkdownContent`) opt into `prose`
     * element styles; plain-text bubbles get matching base typography instead.
     * Content that styles its own elements (e.g. structured content) should
     * stay off `prose`.
     */
    prose: {
      true: {
        bubble: "prose prose-sm prose-tighter",
      },
      false: {
        bubble: "text-sm leading-normal",
      },
    },
    role: {
      user: {
        row: "justify-end",
        bubble: "bg-indigo-800 text-white",
      },
      assistant: {
        row: "justify-start",
        bubble: "bg-gray-100 text-foreground",
      },
      tool: {
        row: "justify-start",
        bubble: "border border-amber-200 bg-amber-50 text-amber-900",
      },
    },
    layout: {
      simulation: {},
      /**
       * The assistant surfaces (research agent, configuration assistants): a
       * muted user bubble above naked assistant text, both left-aligned.
       * Bubbles shrink to their content and are free to grow to the full
       * column — nothing is right-aligned or capped, so a narrow docked panel
       * spends none of its width on a ragged edge.
       *
       * `min-w-0`: the bubble is a flex item, so it would otherwise refuse to
       * shrink below its content's min-content width — a wide table or an
       * unbreakable token would widen the bubble and scroll the whole surface
       * sideways. Shrinking lets long words wrap and leaves wide blocks to
       * scroll within their own row (markdown tables bring their own
       * `overflow-x-auto`).
       */
      agent: {
        row: "justify-start",
        bubble: "max-w-none min-w-0",
      },
    },
    size: {
      [Size.SMALL]: {
        bubble: "text-xs",
      },
      [Size.MEDIUM]: {},
    },
    isSaving: {
      true: {
        bubble: "animate-pulse",
      },
    },
    isError: {
      true: {
        bubble: "bg-red-50 text-red-900",
      },
    },
  },
  compoundVariants: [
    {
      layout: "agent",
      role: "user",
      class: { bubble: "bg-muted text-foreground" },
    },
    {
      layout: "agent",
      role: "assistant",
      class: { bubble: "bg-transparent px-0" },
    },
  ],
  defaultVariants: {
    layout: "simulation",
    prose: false,
  },
});

export const MessageItem: FC<MessageItemProps> = (props) => {
  // Debounce the saving animation so it doesn't happen unless the completion takes a long time
  const isSaving = props.isSaving ?? false;
  const [isSavingDebounced, setIsSavingDebounced] = useState(false);
  useEffect(() => {
    if (!isSaving) {
      setIsSavingDebounced(false);
      return;
    }

    const timeout = setTimeout(
      () => setIsSavingDebounced(isSaving),
      SAVING_INDICATOR_DEBOUNCE_INTERVAL,
    );
    return () => clearTimeout(timeout);
  }, [isSaving]);

  const style = messageItemStyle({
    role: props.role,
    layout: props.layout,
    prose: props.prose,
    size: props.size,
    isSaving: isSavingDebounced,
    isError: props.isError,
  });

  return (
    <div className={style.row()}>
      <div className={style.bubble()}>{props.children}</div>

      {props.isError && props.onRetry ? (
        <div className="mx-2 flex shrink-0 items-center">
          <IconButton
            accessibleLabel="Retry"
            icon={<IconRefresh className="m-1 size-5" />}
            variant={ButtonVariant.Tertiary}
            onClick={props.onRetry}
          />
        </div>
      ) : null}
    </div>
  );
};
